import { useEffect, useId, useState } from "react";
import { getEvents } from "../../lib/api-event";
import type { FamilyEvent } from "../../types/event";
import type { WishEvent } from "../../types/wish";

type PickerState =
  | { status: "loading" }
  | { status: "loaded"; events: FamilyEvent[] }
  | { status: "error" };

interface WishEventPickerProps {
  value: string;
  onChange: (eventID: string) => void;
  disabled?: boolean;
  currentEvent?: WishEvent;
}

export function WishEventPicker({
  value,
  onChange,
  disabled = false,
  currentEvent,
}: WishEventPickerProps) {
  const selectID = useId();
  const [retryKey, setRetryKey] = useState(0);
  const [state, setState] = useState<PickerState>({ status: "loading" });

  useEffect(() => {
    let ignore = false;
    setState({ status: "loading" });
    void getEvents().then(
      (events) => {
        if (!ignore) setState({ status: "loaded", events });
      },
      () => {
        if (!ignore) setState({ status: "error" });
      },
    );
    return () => {
      ignore = true;
    };
  }, [retryKey]);

  return (
    <div>
      <label htmlFor={selectID} className="mb-2 block text-[13px] font-bold text-content">
        Событие (опционально)
      </label>
      {state.status === "error" ? (
        <div className="rounded-2xl border border-[#F4C48F] bg-warning-soft px-4 py-3">
          <p role="alert" className="text-sm font-semibold text-warning">
            Не удалось загрузить события
          </p>
          <button
            type="button"
            onClick={() => setRetryKey((key) => key + 1)}
            disabled={disabled}
            className="mt-2 text-sm font-bold text-content underline disabled:cursor-not-allowed"
          >
            Повторить
          </button>
        </div>
      ) : (
        <select
          id={selectID}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled || state.status === "loading"}
          className="form-control h-12 w-full rounded-2xl border border-line bg-white focus:outline-none disabled:cursor-not-allowed"
        >
          {state.status === "loading" ? (
            <option value={value}>Загружаем события…</option>
          ) : (
            <>
              <option value="">Без события</option>
              {value &&
                currentEvent &&
                !state.events.some((event) => event.id === value) && (
                  <option value={value} disabled>
                    {currentEvent.title} · больше недоступно
                  </option>
                )}
              {state.events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.title} · {formatEventDate(event.date)}
                </option>
              ))}
            </>
          )}
        </select>
      )}
    </div>
  );
}

function formatEventDate(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
  });
}
